import React, { FC, useRef, useMemo } from 'react';

import { Typography } from '@mui/material';
import Box from '@mui/material/Box/Box';
import { FormikProps } from 'formik';

import useUploadCampaignImages, {
  CampaignImageField,
} from '../../../../hooks/Admin/useUploadCampaignImages';

import UploadCampaignImageSection from '../CreateEditCampaign/UploadCampaignImages/UploadCampaignImageSection';
import { formKeys } from '../../../../helpers/Constants';
import strings from '../../../../localization';

import styles from './styles';
import { emailObjectTypes } from '../../../../hooks/Admin/Campaigns/useConfigureCampaignEmail';

interface ICampaignEmailImage {
  formik: FormikProps<any>;
  name:string;
}

const CampaignEmailImage: FC<ICampaignEmailImage> = ({ formik, name }) => {
  const { fields, onSelect, onRemove } = useUploadCampaignImages(
    formik,
  );
  const selectedFileRef = useRef<string | null>(null);

  const inputRefs = useRef<Record<keyof typeof formKeys, HTMLInputElement | null>>({
    [formKeys.registrationPopup]: null,
    [formKeys.activationEmail]: null,
    [formKeys.bookingEmail]: null,
    [formKeys.registrationEmail]: null,
    [formKeys.existingUserEmail]: null,
    [formKeys.multiBookingEmail]: null,
  });

  const fieldImageMap = {
    [`${formKeys.existingUserEmail}${emailObjectTypes.image}`]: `${formKeys.registrationEmail}${emailObjectTypes.image}`,
    [`${formKeys.multiBookingEmail}${emailObjectTypes.image}`]: `${formKeys.bookingEmail}${emailObjectTypes.image}`,
  };

  const emailTemplateItem = formik.values?.emailTemplateItem;
  const isLeadInviteEmail = emailTemplateItem === formKeys.registrationEmail;

  const activeField = useMemo(
    () =>
      fields.find((f: CampaignImageField) =>
        f.key === emailTemplateItem),

    [fields, emailTemplateItem],
  );

  if (!emailTemplateItem || !activeField) return null;

  const handlePickFile = (key: formKeys) =>
    () => {
      inputRefs.current[key]?.click();
    };

  const handleFileChange = (key: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event?.target?.files?.[0];
      if (file) {
        selectedFileRef.current = key;
        onSelect(key, file);
      }
      if (event?.target) {
        event.target.value = '';
      }
    };

  const handleOpenUri = (key:string) =>
    () => {
      const uri = formik.values[key];
      if (uri) {
        window.open(uri, '_blank', 'noopener,noreferrer');
      }
    };

  const popupSuccessField = {
    key: formKeys.registrationPopup,
    label: strings.registrationSuccessPopupImage,
    recommendedSize: '444 × 135 px',
    isEmail: false,
  };

  const fieldName = fieldImageMap?.[name] ?? name;

  return (
    <Box sx={styles.imageSectionContainer}>
      {isLeadInviteEmail && (
        <Box sx={styles.row}>
          <Typography sx={styles.rowLabel}>{strings.registrationSuccessPopupImage}</Typography>
          <UploadCampaignImageSection
            fieldName={`${formKeys.registrationPopup}${emailObjectTypes.image}`}
            formik={formik}
            field={popupSuccessField}
            onRemove={onRemove}
            handleFileChange={handleFileChange}
            handleOpenUri={handleOpenUri}
            handlePickFile={handlePickFile}
            inputRefs={inputRefs}
          />
        </Box>
      )}
      <Box sx={styles.row}>
        <Typography sx={styles.rowLabel}>{strings.banner}</Typography>
        <UploadCampaignImageSection
          formik={formik}
          field={activeField}
          fieldName={fieldName}
          onRemove={onRemove}
          handleFileChange={handleFileChange}
          handleOpenUri={handleOpenUri}
          handlePickFile={handlePickFile}
          inputRefs={inputRefs}
        />
      </Box>
    </Box>
  );
};

export default CampaignEmailImage;
